import React, { useState, useMemo } from 'react';
import Icon from '../../Icon';

/**
 * "View included tests" modal for a lab test panel — search box + collapsed-
 * by-default category sections, instead of dumping every test flat on the
 * page. Only imported by PatientOrderLabTestPage.jsx today — this is a
 * fresh, standalone component, not a change to the Navigator/Doctor booking
 * flow (PlanCatalogStep.jsx), which keeps its own separate inline-expand
 * behaviour untouched.
 *
 * Checkboxes are informational — "this is included in the fixed panel" —
 * not a build-your-own-panel selector. These are pre-priced Thyrocare SKUs;
 * letting a patient deselect a test here would imply a price change this
 * component has no way to compute or send to the backend.
 */
export default function TestDetailsModal({
  test,
  groups,
  loading,
  failed,
  onRetry,
  onClose,
}) {
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState({});

  const totalTests = useMemo(
    () => (groups || []).reduce((sum, g) => sum + g.tests.length, 0),
    [groups]
  );

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups || [];
    const q = search.trim().toLowerCase();
    return (groups || [])
      .map(g => ({
        ...g,
        tests: g.tests.filter(t =>
          t.name.toLowerCase().includes(q) || t.code.toLowerCase().includes(q)
        ),
      }))
      .filter(g => g.tests.length > 0);
  }, [groups, search]);

  const isGroupOpen = (groupName) =>
    search.trim() ? true : !!openGroups[groupName];

  function toggleGroup(groupName) {
    setOpenGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(20,20,20,0.45)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 14,
          width: '100%',
          maxWidth: 560,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="test-details-modal-title"
      >
        {/* Header */}
        <div style={{ padding: '20px 22px 14px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div id="test-details-modal-title" style={{
                fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700,
                color: 'var(--text-primary)',
              }}>
                {test.name}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                {totalTests} tests
                {(test.rate?.sellingPrice ?? test.rate?.listingPrice) != null && (
                  <> · ₹{test.rate?.sellingPrice ?? test.rate?.listingPrice}</>
                )}
                {test.flags?.isFastingRequired && <> · Fasting required</>}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: 4, color: 'var(--text-muted)', flexShrink: 0,
                fontSize: 22, lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          <div style={{ position: 'relative', marginTop: 14 }}>
            <span style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--text-muted)', pointerEvents: 'none',
            }}>
              <Icon name="search" size={15} />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tests in this panel…"
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px 10px 36px',
                borderRadius: 8, border: '1px solid var(--border)',
                fontSize: 14, fontFamily: 'var(--font-body)',
                outline: 'none',
              }}
            />
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
          {loading ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '30px 10px', color: 'var(--text-muted)', fontSize: 14,
            }}>
              <div className="spinner" style={{ width: 16, height: 16 }} />
              Loading test list…
            </div>
          ) : failed ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '30px 10px', color: 'var(--text-muted)', fontSize: 14,
            }}>
              Couldn't load the test list.
              <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>
                ↺ Retry
              </button>
            </div>
          ) : filteredGroups.length === 0 ? (
            <div style={{ padding: '30px 10px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
              {search.trim() ? `No tests match "${search}".` : 'No test details available.'}
            </div>
          ) : (
            filteredGroups.map((g) => {
              const open = isGroupOpen(g.groupName);
              return (
                <div key={g.groupName} style={{ marginBottom: 8 }}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(g.groupName)}
                    aria-expanded={open}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: open ? 'var(--bg-hover)' : 'var(--bg-subtle)',
                      border: '1px solid var(--border)', borderRadius: 8,
                      padding: '10px 12px', cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        display: 'inline-flex',
                        transform: open ? 'rotate(90deg)' : 'none',
                        transition: 'transform 0.15s',
                      }}>
                        <Icon name="chevronRight" size={14} />
                      </span>
                      <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                        {g.groupName}
                      </span>
                    </span>
                    <span className="badge badge-pending" style={{ fontSize: 11 }}>
                      {g.tests.length} test{g.tests.length !== 1 ? 's' : ''}
                    </span>
                  </button>

                  {open && (
                    <ul style={{ margin: 0, padding: '6px 4px 6px 12px', listStyle: 'none' }}>
                      {g.tests.map(t => (
                        <li
                          key={`${g.groupName}-${t.code}-${t.name}`}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '7px 8px', fontSize: 13.5,
                            borderBottom: '1px solid var(--bg-canvas)',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked
                            disabled
                            aria-label={`${t.name} included`}
                            style={{ accentColor: 'var(--green)', cursor: 'default' }}
                          />
                          <span style={{ flex: 1, color: 'var(--text-primary)' }}>{t.name}</span>
                          <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            {t.code}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 22px', borderTop: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Total: <strong>{totalTests}</strong> tests in this panel
          </div>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}